/**
 * Hook for managing QC inspections for Subcontract Receipts
 * Follows the same pattern as work_order_qc but for subcontract receipts
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  QCParameter,
  QCTemplateWithParameters,
  SubcontractReceiptQCInspection,
  SaveQCInspectionRequest,
  QCInspectionSummary,
  QCInspectionResult,
} from '@/types/subcontracting';

export const useSubcontractReceiptQC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [qcTemplates, setQcTemplates] = useState<QCTemplateWithParameters[]>([]);
  const [inspections, setInspections] = useState<SubcontractReceiptQCInspection[]>([]);
  const [summary, setSummary] = useState<QCInspectionSummary | null>(null);

  /**
   * Fetch all active QC templates with their parameters
   */
  const fetchQCTemplates = useCallback(async (type?: 'Component' | 'Assembly' | 'Operation') => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('qc_templates')
        .select('id, template_code, template_name, type, description, status')
        .eq('status', 'Active')
        .order('template_name');
      
      if (type) {
        query = query.eq('type', type);
      }
      
      const { data: templates, error: templatesError } = await query;
      
      if (templatesError) throw templatesError;
      
      // Fetch parameters for each template from qc_template_parameters
      const templatesWithParams: QCTemplateWithParameters[] = [];
      
      for (const template of templates || []) {
        const { data: params, error: paramsError } = await supabase
          .from('qc_template_parameters')
          .select(`
            parameter_id,
            template_id,
            parameter_name,
            specifications,
            tolerance,
            instrument,
            sequence_no
          `)
          .eq('template_id', template.id)
          .order('sequence_no');
        
        if (paramsError) throw paramsError;
        
        const parameters: QCParameter[] = (params || []).map((p: any) => ({
          id: p.parameter_id,
          template_id: p.template_id,
          parameter_name: p.parameter_name,
          uom_id: undefined,
          uom_code: undefined,
          uom_name: undefined,
          min_spec: p.specifications,
          max_spec: undefined,
          tolerance: p.tolerance,
          method: p.instrument || 'Visual',
          is_required: true,
          sequence_no: p.sequence_no,
        }));
        
        templatesWithParams.push({
          ...template,
          parameters,
        });
      }
      
      setQcTemplates(templatesWithParams);
      return templatesWithParams;
    } catch (error: any) {
      toast({
        title: 'Error fetching QC templates',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Fetch a single QC template with parameters by ID
   */
  const fetchQCTemplateById = useCallback(async (templateId: string): Promise<QCTemplateWithParameters | null> => {
    try {
      setLoading(true);
      
      const { data: template, error: templateError } = await supabase
        .from('qc_templates')
        .select('id, template_code, template_name, type, description, status')
        .eq('id', templateId)
        .single();
      
      if (templateError) throw templateError;
      
      // Use qc_template_parameters table which has the full parameter definitions
      const { data: params, error: paramsError } = await supabase
        .from('qc_template_parameters')
        .select(`
          parameter_id,
          template_id,
          parameter_name,
          specifications,
          tolerance,
          instrument,
          sequence_no
        `)
        .eq('template_id', templateId)
        .order('sequence_no');
      
      if (paramsError) throw paramsError;
      
      const parameters: QCParameter[] = (params || []).map((p: any) => ({
        id: p.parameter_id,
        template_id: p.template_id,
        parameter_name: p.parameter_name,
        uom_id: undefined,
        uom_code: undefined,
        uom_name: undefined,
        min_spec: p.specifications,
        max_spec: undefined,
        tolerance: p.tolerance,
        method: p.instrument || 'Visual',
        is_required: true,
        sequence_no: p.sequence_no,
      }));
      
      return {
        ...template,
        parameters,
      } as QCTemplateWithParameters;
    } catch (error: any) {
      toast({
        title: 'Error fetching QC template',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Fetch QC inspections for a receipt
   */
  const fetchInspections = useCallback(async (receiptId: string): Promise<SubcontractReceiptQCInspection[]> => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('subcontract_receipt_qc_inspections_with_details')
        .select('*')
        .eq('receipt_id', receiptId)
        .order('sequence_no');

      if (error) throw error;

      setInspections(data || []);
      return data || [];
    } catch (error: any) {
      toast({
        title: 'Error fetching QC inspections',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Calculate QC summary from inspections
   */
  const calculateSummary = useCallback((
    receiptId: string,
    inspectionData: SubcontractReceiptQCInspection[]
  ): QCInspectionSummary => {
    const passed = inspectionData.filter(i => i.result === 'Pass').length;
    const failed = inspectionData.filter(i => i.result === 'Fail').length;
    const pending = inspectionData.filter(i => i.result === 'Pending').length;
    const notApplicable = inspectionData.filter(i => i.result === 'NA').length;
    const total = inspectionData.length;

    // Determine overall result
    let overallResult: 'Pass' | 'Fail' | 'Pending' = 'Pending';
    if (failed > 0) {
      overallResult = 'Fail';
    } else if (pending === 0 && total > 0) {
      overallResult = 'Pass';
    }

    const completedCount = passed + failed + notApplicable;
    const completedPercentage = total > 0 ? Math.round((completedCount / total) * 100) : 0;

    const summaryData: QCInspectionSummary = {
      receipt_id: receiptId,
      qc_template_id: inspectionData[0]?.qc_template_id,
      template_name: inspectionData[0]?.template_name,
      total_parameters: total,
      passed,
      failed,
      pending,
      not_applicable: notApplicable,
      overall_result: overallResult,
      completed_percentage: completedPercentage,
      inspector_name: inspectionData[0]?.inspector_name,
      inspection_date: inspectionData[0]?.inspection_date,
    };

    setSummary(summaryData);
    return summaryData;
  }, []);

  /**
   * Initialize QC inspections from template parameters
   * Creates pending inspection records for each parameter
   */
  const initializeInspections = useCallback(async (
    receiptId: string,
    templateId: string,
    inspectorName: string
  ): Promise<boolean> => {
    try {
      setLoading(true);

      // Get template with parameters
      const template = await fetchQCTemplateById(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      if (template.parameters.length === 0) {
        throw new Error('Template has no parameters defined');
      }

      // Check if inspections already exist
      const { data: existing } = await supabase
        .from('subcontract_receipt_qc_inspections')
        .select('id')
        .eq('receipt_id', receiptId)
        .limit(1);

      if (existing && existing.length > 0) {
        // Already initialized
        return true;
      }

      // Create inspection records for each parameter
      const inspectionRecords = template.parameters.map(param => ({
        receipt_id: receiptId,
        qc_template_id: templateId,
        parameter_id: param.id,
        result: 'Pending' as QCInspectionResult,
        uom_id: param.uom_id || null,
        inspector_name: inspectorName,
        inspection_date: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('subcontract_receipt_qc_inspections')
        .insert(inspectionRecords);

      if (error) throw error;

      // Fetch the created inspections
      await fetchInspections(receiptId);

      return true;
    } catch (error: any) {
      toast({
        title: 'Error initializing QC inspections',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchQCTemplateById, fetchInspections, toast]);

  /**
   * Update a single inspection result
   */
  const updateInspection = useCallback(async (
    inspectionId: string,
    data: {
      result: QCInspectionResult;
      measurement_value?: number | null;
      remarks?: string;
      deviation_reason?: string;
    }
  ): Promise<boolean> => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('subcontract_receipt_qc_inspections')
        .update({
          result: data.result,
          measurement_value: data.measurement_value,
          remarks: data.remarks,
          deviation_reason: data.deviation_reason,
          inspection_date: new Date().toISOString(),
        })
        .eq('id', inspectionId);

      if (error) throw error;

      return true;
    } catch (error: any) {
      toast({
        title: 'Error updating inspection',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Save all QC inspection results at once
   */
  const saveInspections = useCallback(async (
    request: SaveQCInspectionRequest
  ): Promise<boolean> => {
    try {
      setLoading(true);

      // Update each inspection
      for (const inspection of request.inspections) {
        const { error } = await supabase
          .from('subcontract_receipt_qc_inspections')
          .update({
            result: inspection.result,
            measurement_value: inspection.measurement_value,
            uom_id: inspection.uom_id,
            remarks: inspection.remarks,
            deviation_reason: inspection.deviation_reason,
            inspector_name: request.inspector_name,
            inspection_date: new Date().toISOString(),
          })
          .eq('receipt_id', request.receipt_id)
          .eq('parameter_id', inspection.parameter_id);

        if (error) throw error;
      }

      toast({
        title: 'QC inspections saved',
        description: 'All inspection results have been recorded.',
        variant: 'success',
      });

      return true;
    } catch (error: any) {
      toast({
        title: 'Error saving inspections',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Complete QC and update receipt status based on inspection results
   */
  const completeQCInspection = useCallback(async (
    receiptId: string,
    qcRemarks?: string
  ): Promise<{ success: boolean; status: 'QC Approved' | 'QC Rejected' }> => {
    try {
      setLoading(true);

      // Fetch all inspections
      const inspectionData = await fetchInspections(receiptId);

      // Check if all inspections are complete
      const pendingCount = inspectionData.filter(i => i.result === 'Pending').length;
      if (pendingCount > 0) {
        throw new Error(`${pendingCount} inspection(s) still pending. Please complete all checks.`);
      }

      // Calculate overall result
      const summary = calculateSummary(receiptId, inspectionData);
      const newStatus = summary.overall_result === 'Fail' ? 'QC Rejected' : 'QC Approved';

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Update receipt status
      const { error } = await supabase
        .from('subcontract_receipts')
        .update({
          status: newStatus,
          qc_date: new Date().toISOString(),
          qc_by: user?.id,
          qc_remarks: qcRemarks || `${summary.passed} passed, ${summary.failed} failed out of ${summary.total_parameters} parameters`,
        })
        .eq('id', receiptId);

      if (error) throw error;

      toast({
        title: newStatus === 'QC Approved' ? 'QC Approved' : 'QC Rejected',
        description: newStatus === 'QC Approved'
          ? 'All quality checks passed. Receipt approved.'
          : `${summary.failed} parameter(s) failed quality check.`,
        variant: newStatus === 'QC Approved' ? 'success' : 'destructive',
      });

      return { success: true, status: newStatus };
    } catch (error: any) {
      toast({
        title: 'Error completing QC',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, status: 'QC Rejected' };
    } finally {
      setLoading(false);
    }
  }, [fetchInspections, calculateSummary, toast]);

  /**
   * Check if measurement is within specification
   */
  const isWithinSpec = useCallback((
    value: number | null | undefined,
    minSpec: number | null | undefined,
    maxSpec: number | null | undefined,
    tolerance: number | null | undefined
  ): boolean => {
    if (value === null || value === undefined) return false;

    // If min/max specs are defined
    if (minSpec !== null && minSpec !== undefined && value < minSpec) return false;
    if (maxSpec !== null && maxSpec !== undefined && value > maxSpec) return false;

    // If tolerance is defined (for nominal values), check against tolerance
    // This is typically used when min/max are the same (nominal dimension)
    if (tolerance !== null && tolerance !== undefined) {
      if (minSpec !== null && minSpec !== undefined) {
        if (Math.abs(value - minSpec) > tolerance) return false;
      }
    }

    return true;
  }, []);

  return {
    loading,
    qcTemplates,
    inspections,
    summary,
    fetchQCTemplates,
    fetchQCTemplateById,
    fetchInspections,
    calculateSummary,
    initializeInspections,
    updateInspection,
    saveInspections,
    completeQCInspection,
    isWithinSpec,
  };
};
